import React, { Fragment, useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import GoogleMapReact from 'google-map-react';
import { Tabs } from '../../../2_Molecules/Tabs/Tabs';
import DealerCard from '../../../2_Molecules/DealerCard/DealerCard';
import Marker from '../../../1_Atoms/Marker/Marker';
import Spinner from '../../../1_Atoms/Spinner/Spinner';
import { languageController } from '../../../../controllers/language/languageController';
import { setItemOnLocalStorage } from '../../../../utils/helpers';
import { localStorageKeys } from '../../../../utils/constants';
import generalController from '../../../../controllers/generalController';

function DealerMap({
  dealers,
  searchDealers,
  setMap,
  map,
  setIsMapLoaded,
  isStepRenderType,
  isEmbeddedOnBnP,
  onSelectDealer,
  BaSALink,
  BaTDLink,
  GaQLink,
  currentLang
}) {
  const [activeTab, setActiveTab] = useState('list')
  const [activeDealer, setActveDealer] = useState('')
  const [selectedDealer, setSelectedDealer] = useState('')
  const [isMapUpdating, setIsMapUpdating] = useState(false)
  const [userCurrentPos, setUserCurrentPos] = useState({ lat: 0, lng: 0 })

  const [scrollbar, setScrollbar] = useState(null)
  const scrollWrapper = useRef(null)
  const cardsRef = useRef([])

  // Set the first dealer (closest/most relavant) as active.
  useEffect(() => {
    if (dealers.length > 0) {
      setActveDealer(dealers[0].dealer_code);
      setUserCurrentPos({
        lat: map.getCenter().lat(),
        lng: map.getCenter().lng()
      });
      // Init cardsRef array;
      cardsRef.current = [];
    }
  }, [dealers])

  useEffect(() => {
    // timeoutId for debounce mechanism
    let timeoutId = null;

    // Handle different auto-scrolling behaviour on small vs large devices.
    const resizeListener = () => {
      // Prevent execution of previous setTimeout
      clearTimeout(timeoutId);
      // Run handleSmoothScroller after 150 milliseconds.
      timeoutId = setTimeout(() => {
        const isSmallDevice = ['xs', 'sm', 'md'].includes(generalController.getMediaQuery());
        handleSmoothScroller(isSmallDevice);
      }, 150);
    };

    // Set resize listener
    window.addEventListener('resize', resizeListener);

    return () => {
      // Remove resize listener
      window.removeEventListener('resize', resizeListener);
    }
  }, [])

  // Initialize Scrollbar once all the dear cards are loaded.
  useEffect(() => {
    if (!cardsRef.current || !dealers.length > 0) return;
    // Check if the last dealer card exists in the ref.
    if (cardsRef.current[dealers.length - 1]) {
      const isSmallDevice = ['xs', 'sm', 'md'].includes(generalController.getMediaQuery());
      handleSmoothScroller(isSmallDevice)
    }
  }, [cardsRef.current.length])

  // Handle smooth-scroller instance in setScrollbar.
  const handleSmoothScroller = (isSmallDevice = false) => {
    let scrollbarObj;

    // If it is small device, use scrollbar instance on viewport element.
    if (isSmallDevice) {
      const bodyViewportEl = document.querySelector('.viewport');
      scrollbarObj = window.Scrollbar.get(bodyViewportEl)
    }
    // Otherwise, create a new scrollbar on scrollWrapper.
    else {
      scrollbarObj = window.Scrollbar.init(
        scrollWrapper.current,
        {
          damping: 0.1,
          alwaysShowTracks: true
        }
      );
    }
    setScrollbar(scrollbarObj)
  }

  // Handle dealers update when map is dragged/zoomed
  const handleMapChange = async (map) => {
    if (!map) return;

    setIsMapUpdating(true)

    // Get center coordinates of the updated map
    const coordinates = {
      lat: map.getCenter().lat(),
      lng: map.getCenter().lng()
    }

    // Get new dealers with the new coordinates.
    await searchDealers(coordinates, 'geolocation')

    setIsMapUpdating(false)
  }

  const handleDealerSelect = (dealerData) => {
    // If the selected dealer is the currently selected one, return.
    if (dealerData.dealer_code === selectedDealer) return;

    // Set dealer data
    onSelectDealer({
      dealer: dealerData,
      userPosition: userCurrentPos
    })

    // Set the dealer as the selected
    setSelectedDealer(dealerData.dealer_code)

    // Handle storing selected dealer data on localstorage.
    setItemOnLocalStorage(localStorageKeys.DEALER_SELECTED_BY_USER, dealerData)
  }

  return (
    <div className="mz-dealer-map">
      <div className="mz-container">
        <div className="mz-dealer-map__inner grid-inner-content">
          <Tabs
            setVisibleTab={(id) => setActiveTab(id)}
            activeTab={activeTab}
            dataTabs={[
              {
                id: 'list',
                tabTitle: languageController.getCopy('findDealer', 'mobileList'),
              },
              {
                id: 'map',
                tabTitle: languageController.getCopy('findDealer', 'mobileMap'),
              },
            ]}
          />
          <div className={`mz-dealer-map__wrapper ${activeTab === 'map' ? 'active-map' : ''}`}>
            <Fragment>
              <div
                className="mz-dealer-map__dealers"
                ref={scrollWrapper}
                data-scrollbar
              >
                <div className="mz-dealer-map__dealers--inner">
                  {
                    dealers.length > 0 && (
                      dealers.map((dealer, index) => (
                        <DealerCard
                          cardsRef={cardsRef}
                          key={dealer.dealer_code}
                          dealerData={dealer}
                          isLean={isStepRenderType}
                          index={index + 1}
                          isActive={(dealer.dealer_code === activeDealer)}
                          onClick={() => { setActveDealer(dealer.dealer_code) }}
                          userCurrentPos={userCurrentPos}
                          isSelected={(dealer.dealer_code === selectedDealer)}
                          onSelect={() => handleDealerSelect(dealer)}
                          BaSALink={BaSALink}
                          BaTDLink={BaTDLink}
                          GaQLink={GaQLink}
                          isEmbeddedOnBnP={isEmbeddedOnBnP}
                          currentLang={currentLang}
                        />
                      ))
                    )
                  }
                </div>
              </div>
              <div className="mz-dealer-map__map-widget">
                <GoogleMapReact
                  bootstrapURLKeys={{
                    key: process.env.REACT_APP_GOOGLE_MAP_KEY,
                  }}
                  defaultCenter={{ lat: 0, lng: 0 }}
                  zoom={9}
                  options={{
                    minZoom: 8,
                    maxZoom: 17,
                    fullscreenControl: false
                  }}
                  yesIWantToUseGoogleMapApiInternals
                  onDragEnd={() => handleMapChange(map)}
                  onZoomAnimationEnd={() => handleMapChange(map)}
                  onGoogleApiLoaded={({ map }) => {
                    // Set Google Map instance
                    setMap(map)
                    // Set isMapLoaded true
                    setIsMapLoaded(true)
                  }}
                >
                  {
                    dealers.map((dealer, index) => (
                      <Marker
                        key={dealer.dealer_code}
                        lat={dealer.geolocation.latitude}
                        lng={dealer.geolocation.longitude}
                        isActive={(dealer.dealer_code === activeDealer)}
                        index={index + 1}
                        ariaLabel={`${index + 1}. ${dealer.name}`}
                        onClick={() => {
                          setActveDealer(dealer.dealer_code)
                          // Switch to list view (for small devices).
                          setActiveTab('list')
                          // Scroll into the selected dealer card after the collapse anim is done.
                          setTimeout(() => {
                            scrollbar.scrollIntoView(cardsRef.current[index]);
                          }, 400)
                        }}
                      />
                    ))
                  }
                </GoogleMapReact>
                {
                  isMapUpdating && <Spinner />
                }
              </div>
            </Fragment>
          </div>
        </div>
      </div>
    </div >
  )
}

DealerMap.propTypes = {
  dealers: PropTypes.array.isRequired,
  searchDealers: PropTypes.func.isRequired,
  setMap: PropTypes.func.isRequired,
  map: PropTypes.object,
  setIsMapLoaded: PropTypes.func.isRequired,
  isStepRenderType: PropTypes.bool,
  isEmbeddedOnBnP: PropTypes.bool,
  BaSALink: PropTypes.string,
  BaTDLink: PropTypes.string,
  GaQLink: PropTypes.string,
  currentLang: PropTypes.string,
}

DealerMap.defaultProps = {
  isStepRenderType: false,
  isEmbeddedOnBnP: false,
}

export default DealerMap